import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import selectItemStyled from 'components/SelectItem/styles';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  TYPOGRAPHY,
  LINE_HEIGHT,
  FONT_FAMILY
} = Tokens;

// font-weight doesn't change the thickness, so NOKIA_PURE_TEXT_BOLD is used
const YEAR_TYPOGRAPHY = {
  ...TYPOGRAPHY.BODY,
  fontFamily: FONT_FAMILY.NOKIA_PURE_TEXT_BOLD,
  lineHeight: LINE_HEIGHT.LINE_HEIGHT_NONE,
};

const YEAR_HEIGHT = '2rem';
const DAY_CELL_SIZE = '2.25rem';

const UNSELECTABLE_TEXT = css`
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  & * {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }`;

const BORDER = color => css`border: solid 0.125rem ${color}`;

const CALENDAR_DAY_BUTTON_NORMAL = css`
  background-color: ${props => props.$theme.container};
  ${BORDER(props => props.$theme.border)};
  color: ${props => props.$theme.content};
  & [data-parent="button"] * {
    fill: ${props => props.$theme.content};
  }
  & [data-parent="button-text"] {
    color: ${props => props.$theme.content};
  }`;

const CALENDAR_DAY_BUTTON_FOCUSED = css`
  :focus {
    outline: none;
    background-color: ${props => props.$theme.focused.container};
    border-color: ${props => props.$theme.focused.border};
    color: ${props => props.$theme.focused.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.focused.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.focused.content};
    }
  }
`;

const CALENDAR_DAY_BUTTON_HOVERED = css`
  :hover:not(:disabled) {
    background-color: ${props => props.$theme.hovered.container};
    color: ${props => props.$theme.hovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.hovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.hovered.content};
    }
  }
`;

const CALENDAR_DAY_BUTTON_FOCUSED_HOVERED = css`
  :hover:focus:not(:disabled) {
    background-color: ${props => props.$theme.focusedHovered.container};
    border-color: ${props => props.$theme.focusedHovered.border};
    color: ${props => props.$theme.focusedHovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.focusedHovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.focusedHovered.content};
    }
  }
`;

const CALENDAR_DAY_BUTTON_DISABLED = css`
  :disabled {
    color: ${props => props.$theme.disabled.content};
    background-color: ${props => props.$theme.disabled.container};
    border-color: ${props => props.$theme.disabled.border};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.disabled.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.disabled.content};
    }
  }
`;


const CALENDAR_DAY_SELECTED_FOCUSED = css`
  :focus {
    outline: none;
    background-color: ${props => props.$theme.selected.focused.container};
    border-color: ${props => props.$theme.selected.focused.border};
    color: ${props => props.$theme.selected.focused.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.selected.focused.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.selected.focused.content};
    }
  }
`;

const CALENDAR_DAY_SELECTED_HOVERED = css`
  :hover:not(:disabled) {
    background-color: ${props => props.$theme.selected.hovered.container};
    color: ${props => props.$theme.selected.hovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.selected.hovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.selected.hovered.content};
    }
  }
`;

const CALENDAR_DAY_SELECTED_FOCUSED_HOVERED = css`
  :hover:focus:not(:disabled) {
    background-color: ${props => props.$theme.selected.focusedHovered.container};
    border-color: ${props => props.$theme.selected.focusedHovered.border};
    color: ${props => props.$theme.selected.focusedHovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.selected.focusedHovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.selected.focusedHovered.content};
    }
  }
`;

const CALENDAR_DAY_SELECTED = css`
  background-color: ${props => props.$theme.selected.container};
  color: ${props => props.$theme.selected.content};
  ${CALENDAR_DAY_SELECTED_FOCUSED}
  ${CALENDAR_DAY_SELECTED_HOVERED}
  ${CALENDAR_DAY_SELECTED_FOCUSED_HOVERED}
`;


const CALENDAR_DAY_ERROR_FOCUSED = css`
  :focus {
    outline: none;
    background-color: ${props => props.$theme.error.focused.container};
    border-color: ${props => props.$theme.error.focused.border};
    color: ${props => props.$theme.error.focused.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.error.focused.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.error.focused.content};
    }
  }
`;

const CALENDAR_DAY_ERROR_HOVERED = css`
  :hover:not(:disabled) {
    background-color: ${props => props.$theme.error.hovered.container};
    color: ${props => props.$theme.error.hovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.error.hovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.error.hovered.content};
    }
  }
`;

const CALENDAR_DAY_ERROR_FOCUSED_HOVERED = css`
  :hover:focus:not(:disabled) {
    background-color: ${props => props.$theme.error.focusedHovered.container};
    border-color: ${props => props.$theme.error.focusedHovered.border};
    color: ${props => props.$theme.error.focusedHovered.content};
    & [data-parent="button"] * {
      fill: ${props => props.$theme.error.focusedHovered.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.error.focusedHovered.content};
    }
  }
`;

const CALENDAR_DAY_ERROR = css`
  background-color: ${props => props.$theme.error.container};
  color: ${props => props.$theme.error.content};
  ${CALENDAR_DAY_ERROR_FOCUSED}
  ${CALENDAR_DAY_ERROR_HOVERED}
  ${CALENDAR_DAY_ERROR_FOCUSED_HOVERED}
`;

const DROPDOWN_INDICATOR_STYLE = css`
  ${selectItemStyled.DropDownIndicator} {
    box-sizing: content-box;
  }
`;

export default {
  CalendarContent: styled.div`
    ${DEEP_BORDER_BOX}
    position: relative;
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%
  `,
  CalendarHeader: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex: 1;
    height: 100%;
    flex-direction: column;
    background-color: ${({ $theme, error }) => error ? $theme.error.background : $theme.background};
    min-height: ${SPACING.SPACING_8};
    flex-shrink: 0;
    width: 100%
  `,
  Year: styled.span`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: inline-flex;
    align-items: center;
    overflow: hidden;
    height: ${YEAR_HEIGHT};
    max-width: 100%;
    color: ${({ $theme }) => $theme.content};
    margin: ${SPACING.SPACING_12} ${SPACING.SPACING_8} ${SPACING.SPACING_0} ${SPACING.SPACING_8};
    padding: ${SPACING.SPACING_0} ${SPACING.SPACING_12};
    ${YEAR_TYPOGRAPHY}
  `,
  YearButtonWrapper: styled.span`
    ${DEEP_BORDER_BOX}
    margin: ${SPACING.SPACING_12} ${SPACING.SPACING_8} ${SPACING.SPACING_0} ${SPACING.SPACING_8}
  `,
  Title: styled.span`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    ${css(TYPOGRAPHY.TITLE_16)};
    color: ${({ $theme }) => $theme.TITLE.content};
    background-color: ${({ $theme }) => $theme.TITLE.background};
    padding: ${SPACING.SPACING_8} ${SPACING.SPACING_24} ${SPACING.SPACING_8} ${SPACING.SPACING_24};
    flex: 1
  `,
  Header: styled.span`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    ${css(TYPOGRAPHY.TITLE_24)};
    font-size: 1.2rem;
    color: ${({ $theme }) => $theme.content};
    padding: ${SPACING.SPACING_12} ${SPACING.SPACING_24} ${SPACING.SPACING_24} ${SPACING.SPACING_24};
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  CalendarTimePicker: styled.div`
    ${DEEP_BORDER_BOX}
    display: flex;
    direction: ltr; // time picker should always be LTR, even for RTL languages
    justify-content: ${({ $rtl }) => ($rtl ? 'flex-end' : 'flex-start')}; // time picker should always be LTR, even for RTL languages
    padding: ${SPACING.SPACING_0} ${SPACING.SPACING_24};
    background-color: ${({ $theme }) => $theme.background};
    & > * {
      margin-top: ${SPACING.SPACING_12};
      margin-bottom: ${SPACING.SPACING_12};
      margin-left: ${({ $rtl }) => $rtl ? SPACING.SPACING_16 : SPACING.SPACING_0};
      margin-right: ${({ $rtl }) => $rtl ? SPACING.SPACING_0 : SPACING.SPACING_16};
    }
  `,
  CalendarHourPicker: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-direction: column;
    width: 4.3rem;
    ${DROPDOWN_INDICATOR_STYLE}
  `,
  CalendarMinutePicker: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-direction: column;
    width: 4.3rem;
    ${DROPDOWN_INDICATOR_STYLE}
  `,
  CalendarSecondPicker: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-direction: column;
    width: 4.3rem;
    ${DROPDOWN_INDICATOR_STYLE}
  `,
  CalendarPeriodPicker: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    flex-direction: column;
    width: 4.3rem;
    ${DROPDOWN_INDICATOR_STYLE}
  `,
  CalendarMonthPicker: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex: 1;
    ${css(Tokens.TYPOGRAPHY.TITLE_16)}
    padding: ${SPACING.SPACING_8} ${SPACING.SPACING_32};
    background-color: ${({ $theme }) => $theme.background};
    color: ${({ $theme }) => $theme.content};
    width: 100%;
    max-height: 3rem;
  `,
  CalendarMonthPickerTitle: styled.div`
    ${DEEP_BORDER_BOX}
    padding-bottom: ${SPACING.SPACING_2};
  `,
  CalendarFooter: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    display: flex;
    justify-content: flex-end;
    padding: ${SPACING.SPACING_16};
  `,
  CalendarYearPicker: styled.div`
    ${DEEP_BORDER_BOX}
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    width: 100%;
    height: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: ${SPACING.SPACING_2} ${SPACING.SPACING_0};
    ${UNSELECTABLE_TEXT}
  `,
  CalendarYearPickerListContainer: styled.div`
    ${DEEP_BORDER_BOX}
    padding: ${SPACING.SPACING_4} ${SPACING.SPACING_0};
    max-height: calc(100% - 5rem);
    flex: 0;
    width: 100%;
  `,
  CalendarDayPicker: styled.div`
    ${DEEP_BORDER_BOX}
    position: relative;
    display: flex;
    max-width: 19.5rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: ${SPACING.SPACING_0} ${SPACING.SPACING_32} ${SPACING.SPACING_16} ${SPACING.SPACING_32};
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    ${UNSELECTABLE_TEXT}
  `,
  CalendarDay: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => ($rtl ? 'rtl' : 'ltr')};
    height: ${DAY_CELL_SIZE};
    max-height: ${DAY_CELL_SIZE};
    min-height: ${DAY_CELL_SIZE};
    width: ${DAY_CELL_SIZE};
    max-width: ${DAY_CELL_SIZE};
    min-width: ${DAY_CELL_SIZE};
    ${props => props.inRange ? css`
      background-color: ${props.$theme.inRange.container};
    ` : css``};
    ${props => props.firstInRange ? css`
      background-color: ${props.$theme.inRange.container};
      border-radius: ${({ $rtl }) => ($rtl ? `${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_0}` : `${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_CIRCLE}`)};
    ` : css``};
    ${props => props.lastInRange ? css`
      background-color: ${props.$theme.inRange.container};
      border-radius:  ${({ $rtl }) => ($rtl ? `${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_CIRCLE}` : `${Tokens.RADIUS.RADIUS_0} ${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_CIRCLE} ${Tokens.RADIUS.RADIUS_0}`)};
    ` : css``};
    ${props => props.lastInRange && props.firstInRange ? css`
      background-color: ${props.$theme.inRange.container};
      border-radius: ${Tokens.RADIUS.RADIUS_CIRCLE}
    ` : css``};
  `,
  CalendarEmptyDay: styled.div`
    ${DEEP_BORDER_BOX}
    height: ${DAY_CELL_SIZE};
    max-height: ${DAY_CELL_SIZE};
    min-height: ${DAY_CELL_SIZE};
    width: ${DAY_CELL_SIZE};
    max-width: ${DAY_CELL_SIZE};
    min-width: ${DAY_CELL_SIZE};
  `,
  Week: styled.div`
    ${DEEP_BORDER_BOX}
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin: ${SPACING.SPACING_2} ${SPACING.SPACING_0}
  `,
  Weekdays: styled.div`
    ${DEEP_BORDER_BOX}
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin: ${SPACING.SPACING_8} ${SPACING.SPACING_0}
  `,
  Weekday: styled.span`
    ${DEEP_BORDER_BOX}
    text-align: center;
    width: ${DAY_CELL_SIZE};
    min-width: ${DAY_CELL_SIZE};
    max-width: ${DAY_CELL_SIZE};
    ${css(TYPOGRAPHY.BODY)};
    background-color: ${({ $theme }) => $theme.container};
    color: ${({ $theme }) => $theme.content};
  `,
  CalendarDayButton: styled.button`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => $rtl ? 'rtl' : 'ltr'};
    position: relative;
    display: inline-block;
    outline: none;
    cursor: pointer;
    overflow: hidden;
    height: ${DAY_CELL_SIZE};
    max-height: ${DAY_CELL_SIZE};
    min-height: ${DAY_CELL_SIZE};
    width: ${DAY_CELL_SIZE};
    max-width: ${DAY_CELL_SIZE};
    min-width: ${DAY_CELL_SIZE};
    flex: 0 0 auto;
    margin: 0;
    padding: ${SPACING.SPACING_0};
    border-radius: ${Tokens.RADIUS.RADIUS_CIRCLE};
    z-index: 2;
    :disabled {
      pointer-events: none;
      cursor: initial;
    }
    ${css`${Tokens.TYPOGRAPHY.BODY}`};
    ${CALENDAR_DAY_BUTTON_NORMAL}
    ${CALENDAR_DAY_BUTTON_FOCUSED}
    ${CALENDAR_DAY_BUTTON_HOVERED}
    ${CALENDAR_DAY_BUTTON_FOCUSED_HOVERED}
    ${props => props.selected ? CALENDAR_DAY_SELECTED : css``};
    ${props => props.error ? CALENDAR_DAY_ERROR : CALENDAR_DAY_BUTTON_DISABLED};
  `,
  RangeCell: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => $rtl ? 'rtl' : 'ltr'};
    width: 50%;
    min-width: 50%;
    max-width: 50%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  `,
  RangeSection: styled.div`
    ${DEEP_BORDER_BOX}
    direction: ${({ $rtl }) => $rtl ? 'rtl' : 'ltr'};
    display: flex;
    flex-direction: row;
    align-items: flex-start;
  `
};